// Capacitor import 제거됨 - CapacitorBadge는 App.tsx에서 관리
import { t, Trans } from '@lingui/macro';
import { Bell, MessagesSquare, MessageSquare, ListTodo, LayoutList, Calendar, Building2 } from "lucide-react";
import { useEffect, useState, useRef, createContext, useContext, ReactNode } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { useCalendarContext } from "../../context/CalendarContext";
import { Notification } from "../../models/Notification";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { MyNotifs } from "../../services/MyNotifs";
import { NotificationUtil } from "../../services/Utils/NotificationUtil";
import { SoundUtil } from "../../services/Utils/SoundUtil";
import { UtilApp } from "../../services/Utils/UtilApp";
// CapacitorBadge는 App.tsx에서 반응형으로 관리됨
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { cn } from "../../lib/utils";
import EventDetailOverlay from "../calendar/EventDetailOverlay";
import { fetchEvent } from "../../services/CalendarService";
import { CalendarEvent } from "../../models/CalendarEvent";

const NotificationModal: React.FC<{
  workspaceId?: string | null;
  /** 좌측 메뉴 등 다른 위치에서 재사용 시 트리거 커스터마이즈 (count = 표시할 unread 수) */
  renderTrigger?: (count: number) => ReactNode;
}> = ({ workspaceId = null, renderTrigger }) => {
  const history = useHistory();
  const { setMessageToShow, setIsMobileMenuOpen, workspaces, rooms } = useAppUIContext();
  const { events } = useCalendarContext(); // 이미 로드된 캘린더 일정 — 알림 클릭 시 로딩 생략용
  const { setNotification } = useMessageContext();
  const [open, setOpen] = useState(false);
  const [showClearAlert, setShowClearAlert] = useState(false);
  // 캘린더 알림 클릭 시 라우팅(화면 이동) 대신 이 모달 위에 일정 상세를 바로 띄운다 (채널/캘린더 화면 무관)
  const [popoverEvent, setPopoverEvent] = useState<CalendarEvent | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false); // 클릭 즉시 열고(로딩) → fetchEvent 완료 시 내용 표시
  const popoverAnchorRef = useRef<HTMLElement | null>(null); // 클릭한 알림 항목 = 팝오버 앵커
  const [tab, setTab] = useState<'channel' | 'board' | 'calendar'>('channel');
  // 🔧 React state로 알림 데이터를 관리하여 실시간 re-render 트리거
  const [notifications, setNotifications] = useState<Notification[]>(MyNotifs.Notifications);
  const [nrOfNew, setNrOfNew] = useState(MyNotifs.NrOfNew);

  // ── 통지 센터: enrich(신) + 폴백(구) 으로 소스/채널/워크스페이스 도출 ──
  const multiWs = workspaces.length > 1;
  const wsNameOf = (id?: string | null) => workspaces.find(w => w.id === id)?.name ?? '';
  const wsIdOf = (n: Notification): string | null =>
    n.workspaceId ?? rooms.find(r => r.id === MyNotifs.channelIdOf(n))?.workspaceId ?? null;
  const chNameOf = (n: Notification): string =>
    rooms.find(r => r.id === MyNotifs.channelIdOf(n))?.name ?? n.roomName ?? '';
  const routeOf = (n: Notification): string | null =>
    n.targetRoute ?? (n.roomId ? `/channel/${n.roomId}` : null);

  // 좌측 메뉴 벨(워크스페이스 스코프) 필터 → 없으면 전체.
  // 채널 푸시 알림을 끈(음소거) 채널의 알림은 통지 센터(모달·뱃지·탭 카운트)에서 제외
  // — 좌측 채널 목록의 회색 unread 뱃지는 별도 로직이라 그대로 유지된다.
  const scoped = (workspaceId ? notifications.filter(n => wsIdOf(n) === workspaceId) : notifications)
    .filter(n => MyNotifs.isVisibleInCenter(n, My.RoomSettings));
  // 탭 = 필터 (배타: 채널 스코프 알림(channelId 보유)은 채널 탭에만 노출.
  //  게시판/캘린더 탭은 워크스페이스 스코프(channelId 없음)만 — 채널 게시판/캘린더 알림은
  //  채널 탭에서 확인한다. 같은 알림이 채널·타입 탭에 중복 노출되던 것을 방지.)
  const channelItems = scoped.filter(n => MyNotifs.channelIdOf(n) != null);
  const boardItems = scoped.filter(n => MyNotifs.sourceOf(n) === 'board' && MyNotifs.channelIdOf(n) == null);
  const calendarItems = scoped.filter(n => MyNotifs.sourceOf(n) === 'calendar' && MyNotifs.channelIdOf(n) == null);
  const newCount = (list: Notification[]) => list.filter(n => n.isNew).length;
  const tabItems = tab === 'channel' ? channelItems : tab === 'board' ? boardItems : calendarItems;
  // 트리거 배지 카운트: 워크스페이스 벨이면 해당 ws 의 unread, 전역(프로필) 벨이면 nrOfNew
  const triggerCount = workspaceId ? scoped.filter(n => n.isNew).length : nrOfNew;

  useEffect(() => {
    // play sound on new notification + 🔧 state 업데이트로 실시간 UI 반영
    let unsubNotifs = MyNotifs.subscribeToNotificationUpdates(async (notifs: Notification[]) => {
      // 🔧 React state 업데이트 → re-render 트리거
      setNotifications([...notifs]);
      setNrOfNew(MyNotifs.NrOfNew);

      // 소리는 전역(프로필) 벨만 — 좌측 메뉴 스코프 벨은 중복 재생 방지
      if (!workspaceId && notifs.length > 0 && UtilApp.getPlatform() == 'web' && MyNotifs.NrOfNewLastChange > 0 && !document.hasFocus()) {
        if (notifs[0].type != 'CallStart') {
          if (!My.user.roomSettings?.find(n => n.roomId == notifs[0].roomId && n.disablePushNotifications)) {
            if (notifs[0].type != 'Emoticon') {
              SoundUtil.playNewMessage();
            } else {
              SoundUtil.playNewReaction();
            }
          }
        }
      }
    });

    return () => { unsubNotifs(); };
  }, []);

  function dismissed() {
    Fire.clearNotifications(scoped.filter(n => n.isNew && n.type == 'RoomAdded'));
  }

  // ✅ OS 뱃지는 App.tsx의 subscribeToNewNotifications 콜백에서 반응형으로 관리됨

  function clearAllNotifs() {
    // 현재 탭에 보이는 알림만 확인 처리 (채널 탭=채널, 게시판 탭=게시판, 캘린더 탭=캘린더). 스코프(ws)도 그대로 반영됨
    Fire.clearNotifications(tabItems.filter(n => n.isNew));
    setShowClearAlert(false);
  }

  function itemClick(notif: Notification, e?: React.MouseEvent) {
    // 캘린더 알림: 라우팅 대신 이 모달 위에 일정 상세를 바로 팝오버.
    // 클릭한 알림 항목을 앵커로 삼아 그 위치 기준으로 띄운다. targetRoute "/calendar?event=ID" 에서 eventId 추출.
    if (MyNotifs.sourceOf(notif) === 'calendar') {
      const eventId = new URLSearchParams((notif.targetRoute ?? '').split('?')[1] ?? '').get('event');
      if (eventId) {
        if (e) popoverAnchorRef.current = e.currentTarget as HTMLElement;
        Fire.clearNotifications([notif]);
        // 이미 로드된(캘린더에 보이는) 일정이면 즉시 표시 — 로딩 생략. 없을 때만 fetchEvent + 스켈레톤.
        const cached = events.find((ev) => ev.id === eventId || ev.id.split('::')[0] === eventId) ?? null;
        setPopoverEvent(cached);
        setPopoverOpen(true);
        if (!cached) {
          fetchEvent(eventId).then((ev) => { if (ev) setPopoverEvent(ev); else setPopoverOpen(false); }).catch(() => setPopoverOpen(false));
        }
        return;
      }
    }
    const route = routeOf(notif);
    if (!route) return;
    // 모달과 메뉴 닫기 + 소스별 정확 이동 (채널/게시판/캘린더)
    setOpen(false);
    setIsMobileMenuOpen(false);
    setNotification(notif);
    // 게시판 알림(source='board')은 게시글을 실제 열람할 때 읽음 처리(PostDetailOverlay).
    // 그 외(채팅/캘린더/태스크/방초대)는 클릭 시 읽음 처리.
    if (notif.source !== 'board') Fire.clearNotifications([notif]);
    history.push(route, { fromNotification: true, notification: notif, timestamp: Date.now() });
  }

  async function showMessage(id: string, e: React.MouseEvent) {
    e.stopPropagation();
    Fire.msg.getMessage(id).then(message => {
      if (message) {
        setMessageToShow(message); // MessageModal로 메시지 표시
      } else {
        alert(t`Message could not be found. It may have been deleted.`);
      }
    }).catch(e => {
      console.error(e);
      alert(t`Message could not be found. It may have been deleted.`);
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) dismissed();
      }}>
        <DialogTrigger asChild>
          {renderTrigger ? renderTrigger(triggerCount) : (
            <button
                className="relative p-2 rounded-control border border-border shadow-[0px_2px_5px_1px_rgba(0,0,0,0.04),0px_1px_2px_0px_rgba(0,0,0,0.03)] hover:bg-accent transition-colors"
                data-cy="notif"
              >
              <Bell className="w-6 h-6 text-muted-foreground" />
              {nrOfNew > 0 && (
                <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 h-5 min-w-[1.25rem] px-3 py-3 flex items-center justify-center text-meta-sm bg-primary text-on-primary-text border-0 rounded-full">
                  {nrOfNew}
                </Badge>
              )}
            </button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-2xl h-[80vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-4 pb-1.5 border-b text-left space-y-0">
            {/* Row 1: 타이틀 + 탭 — 데스크탑은 한 줄(탭 우측), 모바일은 탭이 타이틀 아래로 wrap. pr-10 = 닫기 X 자리 */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pr-10">
              <DialogTitle className="text-lg font-semibold shrink-0 flex items-center gap-2">
                {workspaceId ? (
                  <>
                    <Building2 className="size-5 text-admin shrink-0" />
                    {wsNameOf(workspaceId)}
                  </>
                ) : <Trans>Notifications</Trans>}
              </DialogTitle>
              {/* 탭은 필터 — 채널 게시판/캘린더는 채널 탭+타입 탭 모두 노출. flex-1 + justify-center 로 가운데 정렬 */}
              <div className="flex gap-1 flex-1 justify-center">
                <button
                  onClick={() => setTab('channel')}
                  className={cn("flex items-center px-3 py-2 text-sm border-b-2 transition-colors", tab === 'channel' ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground")}
                >
                  <Trans>Channel</Trans>
                  {newCount(channelItems) > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary text-on-primary-text text-meta">{newCount(channelItems)}</span>}
                </button>
                <button
                  onClick={() => setTab('board')}
                  className={cn("flex items-center px-3 py-2 text-sm border-b-2 transition-colors", tab === 'board' ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground")}
                >
                  <Trans>Board</Trans>
                  {newCount(boardItems) > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary text-on-primary-text text-meta">{newCount(boardItems)}</span>}
                </button>
                <button
                  onClick={() => setTab('calendar')}
                  className={cn("flex items-center px-3 py-2 text-sm border-b-2 transition-colors", tab === 'calendar' ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground")}
                >
                  <Trans>Calendar</Trans>
                  {newCount(calendarItems) > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-primary text-on-primary-text text-meta">{newCount(calendarItems)}</span>}
                </button>
              </div>
            </div>
            {/* Row 2: 모두 확인으로 표시 — 탭 아래, 우측 정렬 */}
            <div className="flex justify-end">
              <AlertDialog open={showClearAlert} onOpenChange={setShowClearAlert}>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-primary">
                    <Trans>Mark all as seen</Trans>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      <Trans>Are you sure you want to mark all notifications as seen?</Trans>
                    </AlertDialogTitle>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                    <AlertDialogAction onClick={clearAllNotifs}>
                      <Trans>Yes</Trans>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0">
            {tabItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 px-6 text-center">
                <img
                  src="/assets/img/img-cover-sleep-160.svg"
                  alt="No notifications"
                  className="w-40 h-40 mb-4 opacity-50"
                />
                <p className="text-muted-foreground">
                  <Trans>No notifications</Trans>
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {tabItems.map(notif => {
                  const src = MyNotifs.sourceOf(notif);
                  // 채널 탭바(ChannelTabBar)와 동일 아이콘으로 통일: board=LayoutList, calendar=Calendar, task=ListTodo, chat=MessageSquare
                  const TypeIcon = src === 'calendar' ? Calendar : src === 'board' ? LayoutList : src === 'task' ? ListTodo : MessageSquare;
                  const ch = MyNotifs.channelIdOf(notif);
                  // 스코프(워크스페이스) 모달은 헤더에 ws명이 있으니 항목 라벨 생략 — 전역 모달 + 다중 ws 일 때만 표시
                  const wsLabel = (multiWs && !workspaceId) ? wsNameOf(wsIdOf(notif)) : '';
                  // 제목줄 위치 = #채널명 · 게시판명 (채널 게시판) / 게시판명 (워크스페이스 게시판) / #채널명 (채팅 등).
                  // 본문은 내용만 보여주므로 채널·게시판 맥락을 제목줄에 모은다.
                  const chName = ch ? chNameOf(notif) : '';
                  // 태스크는 보드명(기존 보드는 '인사이트 허브'로 DB에 박제) 대신 'Tasks' 고정. 게시판만 보드명 사용.
                  const boardName = src === 'task' ? t`Tasks` : src === 'board' ? (notif.roomName ?? '') : '';
                  const placeParts: string[] = [];
                  if (chName) placeParts.push(`#${chName}`);
                  if (boardName && boardName !== chName) placeParts.push(boardName);
                  const place = placeParts.join(' · ') || chNameOf(notif);
                  return (
                    <div
                      key={notif.id}
                      className={cn(
                        "flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors border-l-4",
                        notif.isNew ? "border-l-primary bg-primary/15" : "border-l-transparent"
                      )}
                      onClick={(e) => itemClick(notif, e)}
                    >
                      <div className="shrink-0 mt-0.5 flex items-center justify-center w-9 h-9 rounded-full bg-muted text-muted-foreground">
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={cn("flex items-center gap-1.5 text-xs mb-1", notif.isNew && "font-bold")}>
                          {wsLabel && (
                            <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-card-foreground text-meta font-medium">
                              <Building2 className="size-3 shrink-0 text-admin" />
                              {wsLabel}
                            </span>
                          )}
                          <span className="font-semibold text-primary truncate">{src === 'calendar' ? <>{place ? `${place} · ` : ''}<Trans>Event</Trans> · {NotificationUtil.reminderLeadText(notif.minutes)}</> : (place || notif.fromUser?.name)}</span>
                          {notif.fromUser?.name && <span className="text-card-foreground truncate">· {notif.fromUser.name}</span>}
                        </div>
                        <div className="text-sm text-card-foreground line-clamp-2">
                          {NotificationUtil.getSummaryTranslated(notif)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {UtilApp.dateStrToTime(notif.created)}
                        </span>
                        {notif.messageId && (
                          <Button
                            className="w-8 h-8 mt-1"
                            variant="icon-box"
                            size="icon"
                            onClick={(e) => showMessage(notif.messageId!, e)}
                          >
                            <MessagesSquare />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {popoverOpen && (
        <EventDetailOverlay
          open={popoverOpen}
          onOpenChange={(v) => { if (!v) { setPopoverOpen(false); setPopoverEvent(null); } }}
          event={popoverEvent}
          anchorRef={popoverAnchorRef}
          align="center"
        />
      )}
    </>
  );
};

interface MessageContextType {
  notification: Notification | null;
  setNotification: (id: Notification | null) => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

export const MessageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);

  return (
    <MessageContext.Provider value={{ notification, setNotification }}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessageContext = (): MessageContextType => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error("useMessageContext must be used within a MessageProvider");
  }
  return context;
};

export default NotificationModal;